import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Form, Field } from 'react-final-form';
import { Input, Table } from 'reactstrap';
import TemplateButton from "@/shared/components/TemplateButton";
import { PEER_RECLAIM_COLUMNS } from "./columns";
import { flashDispatch, flashMessage } from '@/shared/helpers'
import ApiErrorMessage from "@/shared/components/flash/ApiErrorMessage"
import { getReclaimablePeerPoints } from "@/services/user/getReclaimablePeerPoints";

import axios from 'axios'


const Textfield = ({index}) => {
  return (
    <Field name={`notes[${index}]`}>
        {({ input, meta }) => (
            <Input
                placeholder=""
                type="text"
                {...input}
            />
        )}
    </Field>
  )
}

const Checkbox = ({index}) => {
  return (
    <Field name={`reclaim[${index}]`} type="checkbox">
        {({ input, meta }) => (
            <Input
                type="checkbox"
                {...input}
            />
        )}
    </Field>
  )
}

const ReclaimForm = ({ toggle, participant, program, organization }) => {
  const dispatch = flashDispatch()
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  let [data, setData] = useState([]);
  const [saving, setSaving] = useState(false);
  const refreshReclaimableData = () => {
    setLoading(true)
    return getReclaimablePeerPoints(organization.id, program.id, participant.id).then((items) => {
        setData(items)
        setLoading(false)
    })
  }

  const onSubmit = (values, form) => {
    console.log(values)
    const formData ={reclaim: []}
    const submittedRows = []

    Object.keys(values?.reclaim || {}).map( rIndex => {
      const isReclaim = values.reclaim[rIndex] ? true : false
      // Amounts are stored to 4 decimals but can arrive with float noise
      // (e.g. 2.4000000000000004, or 4e-16 for a used-up row). The API
      // rejects the whole request if any row is 0 or less, so drop those,
      // and its posting check only accepts whole numbers or exactly 4 decimals.
      const amount = Math.round(parseFloat(data[rIndex]['amount']) * 10000) / 10000
      if( isReclaim && amount > 0 )
      {
        formData.reclaim.push({
          id: data[rIndex]['id'],
          note: values?.notes ? values.notes[rIndex] : "",
          journal_event_id: data[rIndex]['journal_event_id'],
          amount: amount.toFixed(4),
        })
        submittedRows.push(data[rIndex])
      }
    })

    // Checked-then-unchecked rows leave `values.reclaim` a non-empty array of
    // `false`s, so the check has to be on what actually got queued to submit,
    // not on the raw field array's length.
    if (formData.reclaim.length === 0)
    {
      alert(t("Select something to reclaim"))
      return
    }
    console.log(formData)
    // return;
    // setSaving(true)
    // return
    axios
        .post(`/organization/${organization.id}/program/${program.id}/user/${participant.id}/reclaim-peer-points`, formData)
        .then((res) => {
            console.log(res)
            if (res.status == 200) {
                // The API catches per-item reclaim failures (e.g. peer balance too low)
                // and still responds 200, so success has to be read from the body —
                // a 200 no longer means the points actually came back.
                const results = res.data?.[participant.id] || []
                const failed = results
                    .map((result, i) => ({ result, row: submittedRows[i] }))
                    .filter(({ result }) => !result?.success)

                setSaving(false)

                if (failed.length === 0) {
                    dispatch(flashMessage('reclaimed successfully!', 'alert-success', 'top'))
                    toggle()
                    // window.location.reload()
                } else {
                    const messages = failed.map(({ result, row }) => `${row?.event_name || 'Reclaim'}: ${result?.error}`)
                    dispatch(flashMessage(<ApiErrorMessage errors={{ errors: messages }} />, 'alert-danger', 'top'))

                    // Some rows in this submission already succeeded server-side —
                    // refresh the list and clear the checked state so a resubmit
                    // doesn't resend an already-reclaimed row.
                    form.reset()
                    refreshReclaimableData()
                }
            }
        })
        .catch((err) => {
            //console.log(error.response.data);
            dispatch(flashMessage(<ApiErrorMessage errors={err.response.data} />, 'alert-danger', 'top'))
            setSaving(false)
        });
  };

  const onClickAction = (e) => {
  }

  const columns = React.useMemo(() => PEER_RECLAIM_COLUMNS, []);

  // console.log(data)

  useEffect(() => {
      let mounted = true;
      if ( participant ) {
        setLoading(true);
        getReclaimablePeerPoints(organization.id, program.id, participant.id).then((items) => {
            console.log(items)
            setData(items);
            setLoading(false);
        });
      }
      return () => (mounted = false);
  }, [participant]);

  if (loading || !participant) return t("loading")
  if( !data ) return 'nothing to reclaim!'

  return (
      <div className="points-summary-table">
          <Form
              onSubmit={onSubmit}
          >
              {({ handleSubmit, form, submitting, pristine, values }) => {
                  // console.log(values)
                  return (
                      <form className="form d-flex flex-column justify-content-evenly p-2" onSubmit={handleSubmit}>
                          <Table className="table table-borderless table-striped">
                              <thead>
                                      <tr>
                                          {columns.map((column, i) => (
                                              <th key={`header-${i}`}>
                                                  {column.Header}
                                              </th>
                                          ))}
                                      </tr>
                              </thead>
                              <tbody>
                                  {data.map((row, i) => {
                                      return (
                                        <tr key={`row-${i}`}>
                                          {columns.map((column, j) => {
                                            let value = row[column.accessor]
                                            if(column.accessor === 'awarded')
                                            {
                                              value = new Date(value).toLocaleDateString("en-US", {})
                                            }
                                            if(column.accessor === 'amount')
                                            {
                                              value = parseFloat(value).toFixed(0)
                                            }
                                            if(column.accessor === 'reclaim')
                                            {
                                              value = <Checkbox index={i} />
                                            }
                                            if(column.accessor === 'reason')
                                            {
                                              value = <Textfield index={i} />
                                            }
                                            return (
                                              <td key={`column-${j}`}>
                                                {value}
                                              </td>
                                            )
                                          })}
                                        </tr>
                                      )
                                  })}
                              </tbody>
                          </Table>
                          <div className='d-flex justify-content-end'>
                              <TemplateButton type='submit' text='Reclaim Peer Allocations' />
                          </div>
                      </form>
                  )
              }}
          </Form>
      </div>
  );
};

export default ReclaimForm